import {
  Box,
  Stack,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Popover,
  Grid2,
  CircularProgress,
  Fade,
  Backdrop,
} from "@mui/material";
import { FC, useEffect, useRef, useState } from "react";
import { GET, POST } from "../../request/service";
import { getDeviceList, updateShadowToDevice } from "../../request/api";
import {
  ALERT_TYPE,
  BATTERY_PERCENTAGE,
  MODE_TYPE,
  NO_DATA,
  REPORT_TITLE,
  SIGNAL_STRENGTH,
} from "../../constants/dashboard";

import Siren from "../../assets/images/Siren.svg";
import Falling from "../../assets/images/Falling.svg";
import Still from "../../assets/images/Still.svg"; 
import Sms from "../../assets/images/Sms.svg";
import Battery_Full from "../../assets/images/Battery-Full.svg";
import Battery_Half from "../../assets/images/Battery-Half.svg";
import Battery_Quarter from "../../assets/images/Battery-Quarter.svg";
import Battery_Three_Quarters from "../../assets/images/Battery-Three-Quarters.svg";
import Battery_Charging from "../../assets/images/Charging-Battery.svg";
import Wifi_One_Third from "../../assets/images/Wifi-One-Third.svg";
import Wifi_Two_Thirds from "../../assets/images/Wifi-Two-Thirds.svg";
import Wifi_Full from "../../assets/images/Wifi-Full.svg";
import { cognitoSubject$ } from "../../services/authService";
import { IconRefresh } from "@tabler/icons-react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

interface Dashboard {
  DashboardId: string;
  Name: string;
}

interface DeviceAlert {
  type: number;
  status: number;
  alertDt: Date;
  msg: MSGAlert;
}

interface MSGAlert {
  text: string;
  option: string;
  updatedAt: Date;
}

interface Status {
  type: number;
  gauge: number;
  signalStrength: number;
}

const devicesPage: FC = () => {
  const [currentDashboard, setCurrentDashboard] = useState("");
  const [dashboardList, setDashboardList] = useState([]);
  const [deviceListPageLoading, setDeviceListPageLoading] = useState(true);
  const [deviceTableLoading, setDeviceTableLoading] = useState(false);
  const [optionWidth, setOptionWidth] = useState("300px");
  const [currentDevice, setCurrentDevice] = useState("");
  const [sirenOrFallingDialogOpen, setSirenOrFallingDialogOpen] =
    useState(false);
  const [isDashboardLoading, setIsDashboardLoading] = useState(false);
  const [currentIconIndex, setCurrentIconIndex] = useState(0);
  const [currentIconType, setCurrentIconType] = useState("");
  const [showSmsDialog, setShowSmsDialog] = useState(false);
  const componentRef = useRef<HTMLDivElement | null>(null);
  const [windowSize, setWindowSize] = useState({
    height: window.innerHeight,
  });
  const [deviceList, setDeviceList] = useState<ReturnType<typeof createData>[]>(
    [],
  );
  const dashboardStorageList = JSON.parse(
    localStorage.getItem("dashboardList") || "[]",
  );
  const hasRun = useRef(false);
  const hasBuild = useRef(false);
  const [showNoData, setShowNoData] = useState(true);

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        height: window.innerHeight,
      });
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [window.innerHeight, deviceList]);

  useEffect(() => {
    if (componentRef.current) {
      const rect = componentRef.current.getBoundingClientRect();
      setWindowSize({
        height: window.innerHeight - parseInt(rect.top.toString(), 10) - 30,
      });
    }
  }, [
    document.getElementById("no-data")?.innerHTML,
    window.innerHeight,
    componentRef,
  ]);

  useEffect(() => {
    setDeviceListPageLoading(true);
    const subscription = cognitoSubject$.subscribe((res) => {
      if (res) {
        setDeviceListPageLoading(false);
        getDeviceListPageData(dashboardStorageList[0].DashboardId);
      }
    });
    // if (localStorage.getItem("cognitoSubject") === "true") {
    //   setDeviceListPageLoading(false);
    //   getDeviceListPageData(dashboardStorageList[0].DashboardId);
    // }
    return () => subscription.unsubscribe();
  }, [cognitoSubject$]);

  const columnDef = [
    "Name",
    "Device id",
    "IMEI",
    "Mode",
    "Alert",
    "Status",
    "Last update",
  ];

  const createData = (
    name: string,
    deviceId: string,
    imei: string,
    mode: string,
    alert: DeviceAlert,
    status: Status,
    updatedAt: string,
  ) => {
    return { name, deviceId, imei, mode, alert, status, updatedAt };
  };

  const handleChange = async (event: SelectChangeEvent) => {
    setCurrentDashboard(event.target.value);
    localStorage.setItem("currentDashboardId", event.target.value);
    // setDeviceList([]);
    setShowNoData(true);
    setDeviceListPageLoading(true);
    hasBuild.current = false;
    await getDeviceListPageData(event.target.value);
  };

  useEffect(() => {
    if (hasRun.current) return;
    if (dashboardStorageList && dashboardStorageList.length > 0) {
      // resetDashboardList();
      getDashBoardAutoWidth(dashboardStorageList);
      setDashboardList(dashboardStorageList);
      setCurrentDashboard(dashboardStorageList[0].DashboardId);
      localStorage.setItem(
        "currentDashboardId",
        dashboardStorageList[0].DashboardId,
      );
      setDeviceListPageLoading(true);
      if (localStorage.getItem("cognitoSubject") === "true") {
        getDeviceListPageData(dashboardStorageList[0].DashboardId);
        hasRun.current = true;
      }
    }
  }, []);

  useEffect(() => {
    let timer: any;
    const startTimer = () => {
      timer = setTimeout(() => {
        try {
          console.log("every minute:", new Date().toLocaleTimeString());
          localStorage.getItem("currentDashboardId");
          hasBuild.current = false;
          getDeviceListPageData(
            localStorage.getItem("currentDashboardId")?.toString() || "",
            true,
          );
          setShowSmsDialog(false);
          setSirenOrFallingDialogOpen(false);
        } catch (error) {
          console.error("timer error:", error);
        } finally {
          startTimer();
        }
      }, 60000);
    };

    startTimer();

    return () => clearTimeout(timer);
  }, []);

  const getDashBoardAutoWidth = (list: Dashboard[]) => {
    let autoWidth = 0;
    list.map((dashboard: Dashboard) => {
      dashboard.Name.length > autoWidth
        ? (autoWidth = dashboard.Name.length)
        : null;
    });
    setOptionWidth(autoWidth * 9.5 + "px");
  };

  const getDeviceListPageData = async (
    dashboardId: string,
    needLoading?: boolean,
  ) => {
    if (hasBuild.current) return;
    setDeviceListPageLoading(true);
    if (needLoading) {
      setDeviceListPageLoading(false);
    }

    if (!localStorage.getItem("cognitoSubject")) {
      setDeviceListPageLoading(true);
    }
    try {
      GET(getDeviceList + "?dashboardId=" + dashboardId, {
        timeout: 20000,
      }).then((res: any) => {
        let latestDeviceList: ReturnType<typeof createData>[] = [];
        if (res.deviceList && res.deviceList.length > 0) {
          hasRun.current = true;
          hasBuild.current = true;
          res.deviceList.map((device: any) => {
            latestDeviceList.push(
              createData(
                device.Name,
                device.DeviceId,
                device.Imei,
                device.Modes,
                {
                  type:
                    device.Alert.alertType === ALERT_TYPE.ALARM
                      ? 1
                      : device.Alert.alertType === ALERT_TYPE.FALLING
                        ? 2
                        : device.Alert.alertType === ALERT_TYPE.STILL
                          ? 3
                          : 0,
                  status: device.Modes === MODE_TYPE.ASSIGNED ? 0 : 1,
                  alertDt: device.Alert.alertDt,
                  msg: {
                    text: device.Message ? device.Message.text : "",
                    option: device.Message ? device.Message.option : "",
                    updatedAt: device.Message ? device.Message.updateDt : "",
                  },
                },
                {
                  type: device.Modes === ALERT_TYPE.CHARGING ? 1 : 0,
                  gauge: device.Status.gauge,
                  signalStrength: device.Status.signalStrength,
                },
                device.UpdateDt,
              ),
            );
          });
          // const sortedRows = [...testRows].sort((x: any, y: any) =>
          //   x.name.localeCompare(y.name, undefined, { sensitivity: "base" }),
          // );
          setDeviceList(latestDeviceList);
          setShowNoData(true);
        } else {
          setShowNoData(false);
          console.log("No Device List Data!");
        }
        setDeviceListPageLoading(false);
        setDeviceTableLoading(false);
      });
    } catch (err) {
      console.log("------device error", err);
    }
  };

  const resetDashboardList = () => {
    const existingList = document.getElementById("dashboard-select");
    if (
      existingList &&
      existingList.innerHTML === "" &&
      currentDashboard !== ""
    ) {
      setCurrentDashboard("");
    }
  };

  const buildBatteryIcon = (type: number) => {
    if (type <= BATTERY_PERCENTAGE.LOW) {
      return Battery_Quarter;
    } else if (
      BATTERY_PERCENTAGE.LOW < type &&
      type <= BATTERY_PERCENTAGE.MEDIUM
    ) {
      return Battery_Half;
    } else if (
      BATTERY_PERCENTAGE.MEDIUM < type &&
      type <= BATTERY_PERCENTAGE.HIGH
    ) {
      return Battery_Three_Quarters;
    } else {
      return Battery_Full;
    }
  };

  const buildWifiIcon = (type: number) => {
    if (type < SIGNAL_STRENGTH.LOW) {
      return Wifi_One_Third;
    } else if (SIGNAL_STRENGTH.LOW <= type && type < SIGNAL_STRENGTH.HIGH) {
      return Wifi_Two_Thirds;
    } else {
      return Wifi_Full;
    }
  };

  const clickSirenOrFallingIcon = (index: number, type: string, e: any) => {
    setCurrentIconType(type);
    setShowSmsDialog(false);
    setSirenOrFallingDialogOpen(true);
    setCurrentIconIndex(index);
    setAnchorEl(e.currentTarget);
  };

  const clickSmsIcon = (index: number, e: any) => {
    setShowSmsDialog(true);
    setCurrentIconIndex(index);
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => {
    setSirenOrFallingDialogOpen(false);
    setShowSmsDialog(false);
    setAnchorEl(null);
  };

  const handleDismiss = async () => {
    await POST(
      updateShadowToDevice,
      {
        bodydata: {
          app: {
            alarm: {
              state: 0,
              ts: dayjs().tz(dayjs.tz.guess()).format("YYYY-MM-DDTHH:mm:ssZ"),
              reason: ALERT_TYPE.CANCELLED,
              fallDelayTimer: 20,
            },
          },
        },
        dashboardId: currentDashboard,
        deviceId: currentDevice,
      },
      "",
    ).then(() => {
      setSirenOrFallingDialogOpen(false);
      refreshDeviceListPage();
    });
  };

  const refreshDeviceListPage = async () => {
    // setDeviceListPageLoading(true);
    setDeviceTableLoading(true);
    hasBuild.current = false;
    await getDeviceListPageData(currentDashboard, true);
  };

  const [anchorEl, setAnchorEl] = useState(null);

  const DialogComponent = () => {
    return showSmsDialog ? (
      <Popover
        open={showSmsDialog}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        PaperProps={{
          sx: {
            borderRadius: "6px",
            padding: "20px",
            width: "20%",
          },
        }}
      >
        <Typography gutterBottom>
          Text: {deviceList[currentIconIndex].alert.msg.text}
        </Typography>
        <Typography gutterBottom>
          Option: {deviceList[currentIconIndex].alert.msg.option}
        </Typography>
        <Typography gutterBottom>
          {deviceList[currentIconIndex]?.alert?.msg?.updatedAt
            ? deviceList[currentIconIndex].alert.msg.updatedAt
                .toString()
                .replace(/-/g, "/")
            : ""}
        </Typography>
        <Button
          onClick={handleClose}
          style={{
            border: "1px solid #006464",
            height: "35px",
            left: "65%",
            top: "5px",
          }}
        >
          Confirmed
        </Button>
      </Popover>
    ) : (
      <Popover
        open={sirenOrFallingDialogOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        PaperProps={{
          sx: {
            borderRadius: "6px",
            padding: "17px",
          },
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
          {currentIconType === "siren"
            ? "Manual alerted"
            : currentIconType === "falling"
              ? "Fall detected alert"
              : "Still detection alert"}
        </Typography>

        <Typography variant="body2" sx={{ color: "gray" }}>
          {deviceList[currentIconIndex]?.alert?.alertDt
            ? deviceList[currentIconIndex].alert.alertDt
                .toString()
                .replace(/-/g, "/")
            : ""}
        </Typography>
        <Button
          onClick={handleDismiss}
          style={{
            backgroundColor: "#006464",
            color: "white",
            width: "45%",
            margin: "10px 10px 0px 0px",
            height: "35px",
          }}
        >
          Dismiss
        </Button>
        <Button
          onClick={handleClose}
          style={{
            border: "1px solid #006464",
            width: "45%",
            marginTop: "10px",
            height: "35px",
            left: "5%",
          }}
        >
          Cancle
        </Button>
      </Popover>
    );
  };

  return (
    <>
      <DialogComponent />

      {optionWidth && (
        <Box>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography
              component="h1"
              fontSize="1.3rem"
              fontWeight={400}
              color="rgba(0, 0, 0, 0.6)"
            >
              {REPORT_TITLE.DEVICE_LIST}
            </Typography>
          </Stack>
          <Stack
            direction="row"
            // spacing={3}
            alignItems="center"
            justifyContent="space-between"
            marginTop={"5px"}
            display={"block"}
          >
            <FormControl
              sx={{
                m: 1,
                minWidth: optionWidth,
                margin: 0,
                marginRight: "26px",
                marginBottom: "20px",
              }}
              size="medium"
            >
              <InputLabel id="dashboard-input-label">Dashboards</InputLabel>
              <Select
                labelId="dashboard-select-label"
                id="dashboard-select"
                value={currentDashboard}
                label="Dashboard"
                onChange={handleChange}
                disabled={isDashboardLoading}
              >
                {dashboardList?.map(
                  (item: { DashboardId: string; Name: string }) => (
                    <MenuItem value={item.DashboardId}>
                      {item.Name ?? ""}
                    </MenuItem>
                  ),
                )}
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              size="large"
              onClick={refreshDeviceListPage}
              sx={{
                borderRadius: "50%",
                padding: "12px",
                minWidth: "auto",
                height: "auto",
                width: "auto",
                display: "inline-flex",
                justifyContent: "center",
                alignItems: "center",
                flexShrink: 0,
                marginLeft: "-5px",
                marginTop: "5px",
              }}
            >
              <IconRefresh stroke={1.5} />
            </Button>
            <Stack>
              <Button
                sx={{
                  ml: "auto",
                  width: "100px",
                  fontSize: "16px",
                  marginTop: "-65px",
                  marginBottom: "15px",
                }}
                onClick={refreshDeviceListPage}
              >
                REFRESH
              </Button>
            </Stack>

            {deviceListPageLoading ? (
              <Box
                sx={{
                  height: "78vh",
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Fade
                  in={deviceListPageLoading}
                  style={{
                    transitionDelay: deviceListPageLoading ? "800ms" : "0ms",
                  }}
                  unmountOnExit
                >
                  <CircularProgress />
                </Fade>
              </Box>
            ) : (
              <>
                {showNoData && deviceList.length > 0 && (
                  <TableContainer
                    component={Paper}
                    sx={{
                      position: "relative",
                      boxShadow:
                        "0px 0px 4px 0px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12)",
                    }}
                  >
                    <Backdrop
                      sx={{
                        position: "absolute",
                        zIndex: 2,
                        color: "#006969",
                        background: "rgba(255, 255, 255, 0.7)",
                      }}
                      open={deviceTableLoading}
                    >
                      <CircularProgress color="inherit" />
                    </Backdrop>
                    <Table
                      sx={{
                        minWidth: 650,
                      }}
                      aria-label="caption table"
                    >
                      <TableHead>
                        <TableRow>
                          {columnDef.map((column) => (
                            <TableCell>{column}</TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {deviceList.map((row, index) => (
                          <TableRow
                            key={row.name}
                            sx={{
                              backgroundColor:
                                (row.alert.status === 1 &&
                                  row.alert.type !== 0) ||
                                row.alert.msg.text
                                  ? "#e2f0d9"
                                  : "",
                            }}
                          >
                            <TableCell align="left" component="th" scope="row">
                              {row.name}
                            </TableCell>
                            <TableCell align="left">{row.deviceId}</TableCell>
                            <TableCell align="left">{row.imei}</TableCell>
                            <TableCell align="left">{row.mode}</TableCell>
                            <TableCell align="left">
                              <div style={{ display: "flex" }}>
                                <Button
                                  sx={{ minWidth: "0px !important" }}
                                  disabled={
                                    (row.alert.type === 1 &&
                                      row.alert.status === 0) ||
                                    row.alert.type !== 1
                                  }
                                  onClick={(e) => {
                                    clickSirenOrFallingIcon(index, "siren", e);
                                    setCurrentDevice(row.deviceId);
                                  }}
                                >
                                  <img
                                    src={Siren}
                                    style={{
                                      width: "25px",
                                      // marginRight: "15px",
                                      cursor: "pointer",
                                      filter:
                                        row.alert.type === 1 &&
                                        row.alert.status === 1
                                          ? "invert(25%) sepia(80%) saturate(500%) hue-rotate(140deg) brightness(50%) contrast(90%)"
                                          : "",
                                    }}
                                    alt="Siren"
                                  />
                                </Button>

                                <Button
                                  sx={{ minWidth: "0px !important" }}
                                  disabled={
                                    (row.alert.type === 2 &&
                                      row.alert.status === 0) ||
                                    row.alert.type !== 2
                                  }
                                  onClick={(e) => {
                                    clickSirenOrFallingIcon(
                                      index,
                                      "falling",
                                      e,
                                    );
                                    setCurrentDevice(row.deviceId);
                                  }}
                                >
                                  <img
                                    src={Falling}
                                    style={{
                                      width: "25px",
                                      // marginRight: "15px",
                                      cursor: "pointer",
                                      filter:
                                        row.alert.type === 2 &&
                                        row.alert.status === 1
                                          ? "invert(25%) sepia(80%) saturate(500%) hue-rotate(140deg) brightness(50%) contrast(90%)"
                                          : "",
                                    }}
                                    alt="Falling"
                                  />
                                </Button>

                                <Button
                                  sx={{ minWidth: "0px !important" }}
                                  disabled={
                                    (row.alert.type === 3 &&
                                      row.alert.status === 0) ||
                                    row.alert.type !== 3
                                  }
                                  onClick={(e) => {
                                    clickSirenOrFallingIcon(index, "still", e);
                                    setCurrentDevice(row.deviceId);
                                  }}
                                >
                                  <img
                                    src={Still}
                                    style={{
                                      width: "25px",
                                      cursor: "pointer",
                                      filter:
                                        row.alert.type === 3 &&
                                        row.alert.status === 1
                                          ? "invert(25%) sepia(80%) saturate(500%) hue-rotate(140deg) brightness(50%) contrast(90%)"
                                          : "",
                                    }}
                                    alt="Still"
                                  />
                                </Button>

                                <Button
                                  sx={{ minWidth: "0px !important" }}
                                  disabled={!row.alert.msg.text}
                                  onClick={(e) => clickSmsIcon(index, e)}
                                >
                                  <img
                                    src={Sms}
                                    style={{
                                      width: "25px",
                                      cursor: "pointer",
                                      filter: row.alert.msg.text
                                        ? "invert(25%) sepia(80%) saturate(500%) hue-rotate(140deg) brightness(50%) contrast(90%)"
                                        : "",
                                    }}
                                    alt="Sms"
                                  />
                                </Button>
                              </div>
                            </TableCell>
                            <TableCell align="left">
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "end",
                                  gap: "8px",
                                }}
                              >
                                <img
                                  src={
                                    row.status.type === 0
                                      ? buildBatteryIcon(row.status.gauge)
                                      : Battery_Charging
                                  }
                                  style={{ width: "26px" }}
                                  alt={`Battery_${row.status.gauge}`}
                                />
                                <span style={{ width: "26px" }}>
                                  {row.status.gauge}%
                                </span>

                                <img
                                  src={buildWifiIcon(row.status.signalStrength)}
                                  style={{ width: "26px", marginLeft: "5px" }}
                                  alt={`Wifi_${row.status.signalStrength}`}
                                />
                                <span style={{ width: "26px" }}>
                                  {row.status.signalStrength}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell align="left">
                              {row.updatedAt.toString().replace(/-/g, "/")}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
                {!showNoData && (
                  <Grid2
                    ref={componentRef}
                    id={"no-data"}
                    sx={{
                      // marginTop: "10px",
                      height: `${windowSize.height}px`,
                      width: "100%",
                      display: "grid",
                      alignItems: "center",
                      justifyItems: "center",
                      backgroundColor: "rgba(0, 0, 0, 0.1)",
                      color: "rgba(0, 0, 0, 0.4)",
                      fontSize: 40,
                    }}
                    container
                    spacing={2}
                    columns={16}
                  >
                    {NO_DATA.MESSAGE}
                  </Grid2>
                )}
              </>
            )}
          </Stack>
        </Box>
      )}
    </>
  );
};
export default devicesPage;
